import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '@/lib/supabase';

export default function LoginPage() {
  const [isSignUp, setIsSignUp] = useState(false);
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { signIn, signUp, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const sessionExpired = (location.state as { sessionExpired?: boolean })?.sessionExpired;

  // Reset the fatal auth flag when we reach the login page
  useEffect(() => {
    import('@/lib/supabase').then(({ resetFatalAuthFlag }) => resetFatalAuthFlag());
  }, []);

  useEffect(() => {
    const hash = window.location.hash;
    if (hash && hash.includes('access_token')) {
      if (hash.includes('type=signup')) {
        const checkSession = async () => {
          const { data: { session } } = await supabase.auth.getSession();
          if (session?.user) {
            setSuccess('Email confirmado com sucesso! Redirecionando...');
            setTimeout(() => {
              navigate('/subscription?welcome=true', { replace: true });
            }, 1500);
          }
        };
        checkSession();
      } else if (hash.includes('type=recovery')) {
        // Recovery link landed on login — redirect to reset-password page
        navigate('/reset-password' + hash, { replace: true });
        return;
      }
    }
  }, [navigate]);

  useEffect(() => {
    if (user) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    if (isSignUp) {
      const base = __BASE_PATH__;
      const cleanBase = base.endsWith('/') ? base.slice(0, -1) : base;
      const redirectUrl = window.location.origin + cleanBase + '/login';
      const { error: signUpError, data: signUpData } = await signUp(email, password, fullName, redirectUrl);
      if (signUpError) {
        setError(signUpError);
      } else {
        // If user was auto-logged in (email confirmation disabled), redirect to subscription immediately
        if (signUpData?.session) {
          setSuccess('Conta criada! Redirecionando para planos...');
          setTimeout(() => {
            navigate('/subscription?new=true', { replace: true });
          }, 800);
        } else {
          setSuccess('Conta criada! Verifique seu email para confirmar, depois faça login.');
          setIsSignUp(false);
        }
      }
    } else {
      const { error: signInError } = await signIn(email, password);
      if (signInError) {
        setError(signInError === 'Invalid login credentials' ? 'Email ou senha inválidos.' : signInError);
      } else {
        navigate('/dashboard', { replace: true });
      }
    }
    setLoading(false);
  };

  const features = [
    { icon: 'ri-send-plane-fill', label: 'Envios em Massa', desc: 'Via Cloud API oficial' },
    { icon: 'ri-bar-chart-box-fill', label: 'Relatórios', desc: 'Taxas e métricas em tempo real' },
    { icon: 'ri-contacts-book-fill', label: 'Gestão de Contatos', desc: 'Importação e segmentação' },
    { icon: 'ri-shield-check-fill', label: '100% Oficial', desc: 'Políticas Meta respeitadas' },
  ];

  return (
    <div className="min-h-screen flex bg-background-50">
      {/* Left branding panel */}
      <div className="hidden lg:flex lg:w-[55%] relative overflow-hidden">
        {/* Background layers */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#0d3b2e] via-[#128C7E] to-[#1a5f4a]" />

        {/* Animated circles */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-24 -left-24 w-[420px] h-[420px] rounded-full bg-white/[0.03] blur-3xl animate-pulse" />
          <div className="absolute top-1/3 -right-32 w-[380px] h-[380px] rounded-full bg-[#25D366]/10 blur-3xl" />
          <div className="absolute -bottom-20 left-1/3 w-[320px] h-[320px] rounded-full bg-white/[0.02] blur-3xl" />
        </div>

        {/* Decorative grid pattern */}
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.3) 1px, transparent 1px)',
            backgroundSize: '40px 40px',
          }}
        />

        <div className="relative z-10 flex flex-col justify-center px-16 xl:px-24 text-white">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-10 animate-slideUp">
            <div className="w-11 h-11 rounded-xl bg-white/15 border border-white/20 flex items-center justify-center backdrop-blur-md">
              <i className="ri-whatsapp-fill text-white text-xl" />
            </div>
            <h1 className="text-2xl font-heading font-bold tracking-tight">WZap PRO</h1>
          </div>

          {/* Headline */}
          <div className="mb-6 animate-slideUp">
            <h2 className="text-5xl font-heading font-bold leading-[1.1] tracking-tight">
              Marketing via
              <br />
              WhatsApp{' '}
              <span className="text-[#25D366]">
                simplificado
              </span>
            </h2>
          </div>

          <p className="text-base text-white/80 leading-relaxed max-w-md mb-10">
            Gerencie campanhas, contatos e envios em massa com a API oficial do WhatsApp Business. Tudo em um só lugar, com relatórios completos e automação inteligente.
          </p>

          {/* Feature cards */}
          <div className="grid grid-cols-2 gap-3 animate-slideUp">
            {features.map((f, i) => (
              <div
                key={f.label}
                className="group bg-white/[0.06] hover:bg-white/[0.10] border border-white/[0.08] hover:border-white/20 rounded-xl p-4 backdrop-blur-sm transition-all duration-300 cursor-default"
                style={{ animationDelay: `${i * 80}ms` }}
              >
                <div className="w-9 h-9 rounded-lg bg-[#25D366]/20 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                  <i className={`${f.icon} text-[#25D366] text-lg`} />
                </div>
                <p className="text-sm font-semibold text-white">{f.label}</p>
                <p className="text-xs text-white/60 mt-0.5">{f.desc}</p>
              </div>
            ))}
          </div>

          {/* Bottom stats bar */}
          <div className="mt-10 flex items-center gap-8">
            <div>
              <p className="text-2xl font-heading font-bold text-white">10K+</p>
              <p className="text-xs text-white/60">Campanhas enviadas</p>
            </div>
            <div className="w-px h-10 bg-white/10" />
            <div>
              <p className="text-2xl font-heading font-bold text-white">98.5%</p>
              <p className="text-xs text-white/60">Taxa de entrega</p>
            </div>
            <div className="w-px h-10 bg-white/10" />
            <div>
              <p className="text-2xl font-heading font-bold text-white">24/7</p>
              <p className="text-xs text-white/60">Suporte ativo</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 relative">
        {/* Subtle bg accent */}
        <div className="absolute top-0 right-0 w-[300px] h-[300px] rounded-full bg-primary-100/40 blur-[120px] pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-[200px] h-[200px] rounded-full bg-accent-100/30 blur-[100px] pointer-events-none" />

        <div className="w-full max-w-[420px] relative z-10">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-3 mb-10 justify-center">
            <div className="w-10 h-10 rounded-xl bg-primary-500 flex items-center justify-center">
              <i className="ri-whatsapp-fill text-white text-xl" />
            </div>
            <span className="text-xl font-heading font-bold text-foreground-950">WZap PRO</span>
          </div>

          <div className="mb-8">
            <h2 className="text-3xl font-heading font-bold text-foreground-950">
              {isSignUp ? 'Criar conta' : 'Bem-vindo de volta'}
            </h2>
            <p className="text-sm text-foreground-500 mt-2">
              {isSignUp
                ? 'Preencha os dados abaixo para começar'
                : 'Faça login para acessar seu painel'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {sessionExpired && (
              <div className="bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 rounded-xl px-4 py-3 flex items-center gap-3 animate-fadeIn">
                <i className="ri-timer-line text-amber-600 dark:text-amber-400 text-lg flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-amber-800 dark:text-amber-300">Sessão expirada</p>
                  <p className="text-xs text-amber-600 dark:text-amber-400/80 mt-0.5">Sua sessão foi encerrada por segurança. Faça login novamente.</p>
                </div>
              </div>
            )}

            {error && (
              <div className="bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-xl px-4 py-3 flex items-center gap-3 animate-fadeIn">
                <i className="ri-error-warning-line text-red-500 text-lg flex-shrink-0" />
                <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
              </div>
            )}

            {success && (
              <div className="bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20 rounded-xl px-4 py-3 flex items-center gap-3 animate-fadeIn">
                <i className="ri-check-line text-emerald-500 text-lg flex-shrink-0" />
                <p className="text-sm text-emerald-700 dark:text-emerald-400">{success}</p>
              </div>
            )}

            {isSignUp && (
              <div className="animate-slideUp">
                <label htmlFor="fullName" className="block text-sm font-medium text-foreground-700 dark:text-foreground-300 mb-1.5">
                  Nome completo
                </label>
                <div className="relative">
                  <i className="ri-user-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400 text-lg" />
                  <input
                    id="fullName"
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    className="w-full pl-11 pr-4 py-3 bg-white dark:bg-background-100 border border-background-200 dark:border-background-300 rounded-xl text-sm text-foreground-900 dark:text-foreground-100 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-4 focus:ring-primary-100/50 transition-all"
                    placeholder="Seu nome completo"
                    required={isSignUp}
                    autoComplete="name"
                  />
                </div>
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-foreground-700 dark:text-foreground-300 mb-1.5">Email</label>
              <div className="relative">
                <i className="ri-mail-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400 text-lg" />
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-11 pr-4 py-3 bg-white dark:bg-background-100 border border-background-200 dark:border-background-300 rounded-xl text-sm text-foreground-900 dark:text-foreground-100 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-4 focus:ring-primary-100/50 transition-all"
                  placeholder="seu@email.com"
                  required
                  autoComplete="email"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-foreground-700 dark:text-foreground-300 mb-1.5">Senha</label>
              <div className="relative">
                <i className="ri-lock-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400 text-lg" />
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-11 pr-11 py-3 bg-white dark:bg-background-100 border border-background-200 dark:border-background-300 rounded-xl text-sm text-foreground-900 dark:text-foreground-100 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-4 focus:ring-primary-100/50 transition-all"
                  placeholder="••••••••"
                  required
                  autoComplete={isSignUp ? 'new-password' : 'current-password'}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-foreground-400 hover:text-foreground-600 dark:hover:text-foreground-300 transition-colors cursor-pointer w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 dark:hover:bg-background-200"
                >
                  <i className={`text-lg ${showPassword ? 'ri-eye-off-line' : 'ri-eye-line'}`} />
                </button>
              </div>
            </div>

            {!isSignUp && (
              <div className="flex items-center justify-between text-sm">
                <label className="flex items-center gap-2.5 text-foreground-600 cursor-pointer">
                  <input
                    type="checkbox"
                    className="w-[18px] h-[18px] rounded border-background-300 text-primary-500 focus:ring-primary-400/50 focus:ring-2"
                  />
                  Lembrar-me
                </label>
                <Link to="/forgot-password" className="text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap transition-colors">
                  Esqueceu a senha?
                </Link>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-primary-600 hover:bg-primary-700 disabled:bg-primary-400 text-white rounded-xl text-sm font-semibold transition-all duration-200 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer shadow-sm hover:shadow-md active:scale-[0.98]"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  <span>{isSignUp ? 'Criando conta...' : 'Entrando...'}</span>
                </>
              ) : (
                <>
                  <i className={`text-lg ${isSignUp ? 'ri-user-add-line' : 'ri-login-box-line'}`} />
                  <span>{isSignUp ? 'Criar conta' : 'Entrar'}</span>
                </>
              )}
            </button>
          </form>

          <p className="mt-8 text-center text-sm text-foreground-500 dark:text-foreground-400">
            {isSignUp ? (
              <>
                Já tem conta?{' '}
                <button
                  type="button"
                  onClick={() => { setIsSignUp(false); setError(''); setSuccess(''); }}
                  className="text-primary-600 dark:text-primary-400 hover:text-primary-700 font-semibold transition-colors"
                >
                  Fazer login
                </button>
              </>
            ) : (
              <>
                Não tem conta?{' '}
                <button
                  type="button"
                  onClick={() => { setIsSignUp(true); setError(''); setSuccess(''); }}
                  className="text-primary-600 dark:text-primary-400 hover:text-primary-700 font-semibold transition-colors"
                >
                  Criar conta gratuita
                </button>
              </>
            )}
          </p>
        </div>
      </div>
    </div>
  );
}